import { Component, OnInit, OnDestroy } from '@angular/core';
import { AnalyticsCampaign } from 'src/app/models/AnalyticsCampaign';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { PaginatedResult } from 'src/app/shared/model/paginated-result';
import { Integration } from 'src/app/models/Integration';
import { IntegrationsService } from 'src/app/shared/services/integrations.service';

import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrls: ['../analytics.component.scss']
})
export class AnalyticsCampaignsComponent implements OnInit, OnDestroy {
  filter: string;
  startDate: Date;
  endDate: Date;
  fromAt: string;
  toAt: string;
  campaignName: string;

  integrationId: string;

  campaigns: PaginatedResult<AnalyticsCampaign[]>;
  loading = false;
  integrations: Integration[] = [];

  subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private integrationService: IntegrationsService,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.fromAt = params.from;
        this.toAt = params.to;
        this.startDate = params.from ? new Date(params.from + 'T00:00:00') : null;
        this.endDate = params.to ? new Date(params.to + 'T00:00:00') : null;
        this.campaignName = params.name;
        this.integrationId = params.integration_id || '';

        this.loadFromApi();
      }),
      this.integrationService.listAll().subscribe(integrations => this.integrations = integrations)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get totalSent(): number {
    return this.campaigns?.data?.reduce((sum, c) => sum + (+c.qty_sent || 0), 0) || 0;
  }

  get totalClicks(): number {
    return this.campaigns?.data?.reduce((sum, c) => sum + (+c.qty_clicks || 0), 0) || 0;
  }

  get totalCtr(): number {
    return this.totalSent > 0 ? this.totalClicks / this.totalSent : 0;
  }

  formatNumber(value: number, fallback = '-'): string {
    if (!value || value <= 0) return fallback;
    return value.toLocaleString('pt-BR');
  }

  formatPercent(value: number, fallback = '-'): string {
    if (!value || value <= 0) return fallback;
    return value.toLocaleString('pt-BR', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  loadFromApi(): void {
    this.loading = true;
    this.subscriptions.push(
      this.analyticsService.getCampaigns(this.campaignName, this.fromAt, this.toAt, this.integrationId)
      .subscribe({
        next: result => {
          this.loading = false;
          this.campaigns = result;
        },
        error: () => { this.loading = false; },
      })
    );
  }

  filterBy(): void {
    const queryParams = {
      from: this.formatDate(this.startDate),
      to: this.formatDate(this.endDate),
      name: this.campaignName,
      integration_id: this.integrationId,
    };

    this.router.navigate(['/analytics/campaigns'], { queryParams });
  }

  formatDate(date: Date): string {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  clearFilter(): void {
    this.router.navigate(['/analytics/campaigns']);
    this.filter = null;
  }

  filterByDate(value: string): void {
    let from = new Date();
    let to = new Date();

    this.filter = value;

    if (value == 'yesterday') {
      from.setDate(from.getDate()-1);
      to.setDate(to.getDate()-1);
    }

    if (value == '-7 days') {
      from.setDate(from.getDate()-7);
    }

    if (value == '-15 days') {
      from.setDate(from.getDate()-15);
    }

    if (value == 'this month') {
      from.setDate(1);
      to = new Date(to.getFullYear(), to.getMonth()+1, 0);
    }

    if (value == 'last month') {
      from.setMonth(to.getMonth()-1);
      from.setDate(1);
      to.setMonth(to.getMonth()-1);
      to = new Date(to.getFullYear(), to.getMonth()+1, 0);
    }

    const queryParams = {
      from: this.formatDate(from),
      to: this.formatDate(to),
      name: this.campaignName,
      integration_id: this.integrationId,
    };

    this.router.navigate(['/analytics/campaigns'], { queryParams });
  }
}
